export interface Box { x: number; y: number; w: number; h: number }
export type RectMM = readonly [number, number, number, number]
export interface PageSize { width_mm: number; height_mm: number }
export interface Viewport { x: number; y: number; scale: number }
export type DragHandle = 'move' | 'nw' | 'ne' | 'sw' | 'se'

// object-contain, object-position: center. Offsets are relative to the image element.
export function containViewport(page: PageSize, container: { width: number; height: number }): Viewport {
  const scale = Math.min(container.width / page.width_mm, container.height / page.height_mm)
  return { x: (container.width - page.width_mm * scale) / 2, y: (container.height - page.height_mm * scale) / 2, scale }
}

export function normalizedToPage(box: Box, panel: RectMM): Box {
  return { x: panel[0] + box.x * panel[2], y: panel[1] + box.y * panel[3], w: box.w * panel[2], h: box.h * panel[3] }
}

export function pageToNormalized(box: Box, panel: RectMM): Box {
  return { x: (box.x - panel[0]) / panel[2], y: (box.y - panel[1]) / panel[3], w: box.w / panel[2], h: box.h / panel[3] }
}

export function pageToDisplay(box: Box, viewport: Viewport): Box {
  return { x: viewport.x + box.x * viewport.scale, y: viewport.y + box.y * viewport.scale, w: box.w * viewport.scale, h: box.h * viewport.scale }
}

export function displayToPage(box: Box, viewport: Viewport): Box {
  return { x: (box.x - viewport.x) / viewport.scale, y: (box.y - viewport.y) / viewport.scale, w: box.w / viewport.scale, h: box.h / viewport.scale }
}

export function displayDeltaToNormalized(dx: number, dy: number, panel: RectMM, viewport: Viewport) {
  return { x: dx / viewport.scale / panel[2], y: dy / viewport.scale / panel[3] }
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))
const minimumSize = 0.01

// Quantize to millionths using integer bounds: even serialized edge boxes satisfy SQL CHECKs.
export function clampBox(box: Box): Box {
  const units = 1_000_000
  const w = clamp(Math.round(box.w * units), minimumSize * units, units)
  const h = clamp(Math.round(box.h * units), minimumSize * units, units)
  const x = clamp(Math.round(box.x * units), 0, units - w)
  const y = clamp(Math.round(box.y * units), 0, units - h)
  return { x: x / units, y: y / units, w: Math.min(w / units, 1 - x / units), h: Math.min(h / units, 1 - y / units) }
}

export function dragBox(box: Box, dx: number, dy: number, handle: DragHandle): Box {
  if (handle === 'move') return clampBox({ ...box, x: box.x + dx, y: box.y + dy })
  const left = handle.includes('w') ? clamp(box.x + dx, 0, box.x + box.w - minimumSize) : box.x
  const top = handle.includes('n') ? clamp(box.y + dy, 0, box.y + box.h - minimumSize) : box.y
  const right = handle.includes('e') ? clamp(box.x + box.w + dx, box.x + minimumSize, 1) : box.x + box.w
  const bottom = handle.includes('s') ? clamp(box.y + box.h + dy, box.y + minimumSize, 1) : box.y + box.h
  return clampBox({ x: left, y: top, w: right - left, h: bottom - top })
}
